import { buildApiUrl } from '@mxa-argos/core/config/api';
import { auth } from '@mxa-argos/core/config/firebase';
import type {
  ProgetAppReleaseOutput,
  ProvisioningAppReleasesBatchOutput,
} from '@mxa-argos/core/graphql/graphql';
import { type ArgosOperationContext, graphqlClient } from '@mxa-argos/core/graphql/graphql-client';
import { RequestFailedError } from '@mxa-argos/lib/errors/request-failed.error';
import type { ForjaApk } from '@mxa-argos/modules/forja/interfaces/forja-apk.interface';
import { ForjaError } from '@mxa-argos/modules/forja/lib/forja-error';
import {
  ASSIGN_PROGET_PROVISIONING_GROUP_MUTATION,
  DISABLE_KIOSK_MODE_MUTATION,
  ENABLE_KIOSK_MODE_MUTATION,
  PROVISIONING_APP_RELEASES_QUERY,
  PUSH_MOS_MANAGED_CONFIGURATION_MUTATION,
} from '@mxa-argos/modules/forja/queries/forja.queries';
import type { DocumentNode } from 'graphql';

async function fetchApk(release: ProgetAppReleaseOutput): Promise<Response> {
  const token = await auth.currentUser?.getIdToken();

  if (!token) {
    throw new Error('No active session');
  }

  const response = await fetch(
    buildApiUrl(`/internals/proget-applications/${release.progetApplicationUUID}/latest/apk`),
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!response.ok) {
    throw new RequestFailedError(response.status);
  }

  return response;
}

export async function findProvisioningAppReleases(
  imeis: string[],
  packageNames: string[],
): Promise<ProvisioningAppReleasesBatchOutput> {
  const result = await graphqlClient
    .query<{ provisioningAppReleases: ProvisioningAppReleasesBatchOutput }>(
      PROVISIONING_APP_RELEASES_QUERY,
      {
        deviceIMEIListInput: { values: imeis.map((imei) => ({ value: imei })) },
        packageNameListInput: {
          values: packageNames.map((packageName) => ({ value: packageName })),
        },
      },
    )
    .toPromise();

  if (result.error || !result.data?.provisioningAppReleases) {
    throw new ForjaError(
      'APP_RELEASES_UNRESOLVED',
      result.error?.message ?? 'no releases returned',
    );
  }

  return result.data.provisioningAppReleases;
}

const APK_CACHE_NAME = 'forja-apk-cache';

const inFlightDownloads = new Map<string, Promise<ForjaApk>>();

export function downloadApk(release: ProgetAppReleaseOutput): Promise<ForjaApk> {
  const inFlight = inFlightDownloads.get(release.progetApplicationUUID);

  if (inFlight) {
    return inFlight;
  }

  const download = restoreOrFetchApk(release).finally(() =>
    inFlightDownloads.delete(release.progetApplicationUUID),
  );
  inFlightDownloads.set(release.progetApplicationUUID, download);

  return download;
}

// A Proget application keeps its uuid across uploads, so the cache holds one version per uuid.
async function restoreOrFetchApk(release: ProgetAppReleaseOutput): Promise<ForjaApk> {
  const cache = await caches.open(APK_CACHE_NAME);
  const prefix = `/forja-apk/${release.packageName}/${release.progetApplicationUUID}/`;
  const key = `${prefix}${release.versionCode}`;

  const cached = await cache.match(key);

  if (cached) {
    return { release, blob: await cached.blob(), wasCached: true };
  }

  const blob = await fetchApk(release).then((response) => response.blob());
  const expectedSize = release.sizeBytes;

  if (typeof expectedSize === 'number' && blob.size !== expectedSize) {
    throw new Error(
      `size mismatch for ${release.packageName}: expected ${expectedSize}, got ${blob.size}`,
    );
  }

  await cache.put(key, new Response(blob));

  for (const request of await cache.keys()) {
    const path = new URL(request.url).pathname;

    if (path.startsWith(prefix) && path !== key) {
      await cache.delete(request);
    }
  }

  return { release, blob, wasCached: false };
}

export async function markForProduction(imei: string): Promise<void> {
  await runDeviceMutation(ASSIGN_PROGET_PROVISIONING_GROUP_MUTATION, imei);
}

export async function disableKioskMode(imei: string): Promise<void> {
  await runDeviceMutation(DISABLE_KIOSK_MODE_MUTATION, imei);
}

export async function pushMOSManagedConfiguration(imei: string): Promise<void> {
  await runDeviceMutation(PUSH_MOS_MANAGED_CONFIGURATION_MUTATION, imei);
}

export async function enableKioskMode(imei: string): Promise<void> {
  await runDeviceMutation(ENABLE_KIOSK_MODE_MUTATION, imei);
}

// Rejections are expected here (Proget refuses the kiosk until the production group lands) and
// every failure is already recorded as a step result, so the global error toast only adds noise.
async function runDeviceMutation(mutation: DocumentNode, imei: string): Promise<void> {
  const context: ArgosOperationContext = { shouldSkipErrorToast: true };
  const result = await graphqlClient
    .mutation(mutation, { deviceIMEIInput: { value: imei } }, context)
    .toPromise();

  if (result.error) {
    throw result.error;
  }
}
